import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Table } from '../OrderDetails/components/Table'
import { Button, buttonVariants } from '../../components/shadcn/Button'
import { DateFormatted } from '../../components/DateFormatted'
import { cn } from '../../components/shadcn/utils'
import { FragmentType, graphql, useFragment } from '../../gql'
import { FragmentValue } from '../../lib/RequestUtils'
import { SquareAmountCell } from '../OrderDetails/components/SquareAmountCell'
import { faFilePdf, faTruckLoading } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faFileDownload } from '@fortawesome/pro-light-svg-icons'
import { faTractor } from '@fortawesome/pro-duotone-svg-icons'

export const DisposalTable_DisposalFragment = graphql(`
  fragment DisposalTable_DisposalFragment on PackageDisposal {
    id
    code
    ticketUrl
    sent
    carrier {
      id
      name
    }
    company {
      id
      name
    }

    packages {
      id
      order {
        id
        shippingAddress {
          companyName
          firstName
          lastName
        }
      }
    }
  }
`)

interface DisposalTableProps {
  disposals: FragmentType<typeof DisposalTable_DisposalFragment>[]
  onSendDisposal?: (disposal: FragmentValue<typeof DisposalTable_DisposalFragment>) => void
  isLoading?: boolean
}

const columnHelper = createColumnHelper<FragmentValue<typeof DisposalTable_DisposalFragment>>()

export const DisposalTable: React.FC<DisposalTableProps> = ({ disposals, onSendDisposal, isLoading = false }) => {
  const disposalData = useFragment(DisposalTable_DisposalFragment, disposals)

  const columns = useMemo(
    () => [
      columnHelper.accessor('carrier.name', {
        header: 'Dopravce',
        cell: ({ getValue }) => getValue() || '-',
        size: 200,
      }),
      columnHelper.accessor('code', {
        header: 'Kód svozu',
        cell: ({ getValue }) => getValue() || '-',
        size: 150,
      }),
      columnHelper.display({
        id: 'recipient',
        header: 'Příjemce',
        cell: ({ row }) => {
          const disposal = row.original
          if (disposal.packages?.length > 0) {
            const shippingAddress = disposal.packages[0]?.order?.shippingAddress
            const companyName = shippingAddress?.companyName || ''
            const firstName = shippingAddress?.firstName || ''
            const lastName = shippingAddress?.lastName || ''
            return `${companyName} ${firstName} ${lastName}`.trim() || '-'
          }
          return '-'
        },
        size: 300,
      }),
      columnHelper.accessor('company.name', {
        header: 'Společnost',
        cell: ({ getValue }) => getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('sent', {
        header: 'Datum odeslání',
        cell: ({ getValue }) => <DateFormatted date={getValue()} />,
        size: 150,
      }),
      columnHelper.display({
        id: 'packageCount',
        header: () => <div className="text-center">Počet balíků</div>,
        cell: ({ row }) => <SquareAmountCell>{row.original.packages?.length || 0}</SquareAmountCell>,
        size: 100,
      }),
      columnHelper.accessor('ticketUrl', {
        header: () => <div className="text-center">PDF</div>,
        cell: ({ getValue }) => (
          <div className="flex justify-center items-center flex-1">
            {getValue() ? (
              <a
                href={getValue()}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
                <FontAwesomeIcon icon={faDownload} />
                Stáhnout
              </a>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        ),
        size: 150,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="text-center">Akce</div>,
        cell: ({ row }) => (
          <div className="flex justify-center gap-2 flex-1">
            {!row.original.sent && onSendDisposal && (
              <Button
                variant="default"
                size="sm"
                disabled={isLoading}
                onClick={() => onSendDisposal(row.original)}
                icon={faTruckLoading}
                title="Odeslat svoz">
                Odeslat
              </Button>
            )}
            {row.original.sent && <span className="text-green-600 text-sm font-medium">Odesláno</span>}
          </div>
        ),
        size: 150,
      }),
    ],
    [onSendDisposal, isLoading],
  )

  const table = useReactTable({
    columns,
    data: disposalData,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      // @ts-ignore
      size: '1fr',
    },
  })

  return (
    <div className="max-w-[1400px] rounded-lg border o">
      <Table table={table} />
    </div>
  )
}
